import { Tab, Tabs, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { Link, Outlet, useParams } from 'react-router-dom'

import IconCounterGroup from 'components/rbac-settings/IconCounterGroup'
import { useSettingsQueryContext } from 'components/rbac-settings/SettingsQueryContext'
import useAuth from 'hooks/use-auth'
import theme from 'theme'
import { isPrivateEdition } from 'utils/app-config'
import { getUserDisplayName } from 'utils/user'

const TABS = [
  { path: 'details', value: 0 },
  { path: 'security', value: 1 },
  { path: 'integrations', value: 2 },
  { path: 'apikeys', value: 3 }
]

/**
 * Displays the Account Settings tabbed page content for setting details, security, integrations and api keys.
 */
export const ProfilePage = () => {
  const { session } = useAuth()
  const params = useParams()
  const { company, queryApiKeyResult } = useSettingsQueryContext()
  const { data: apiKeys } = queryApiKeyResult
  const areApiKeysEnabled = company?.auth_method_prefs?.api_keys_enabled
  const teams = session?.teams ?? []
  const hasFirstOrLastName = 'first_name' in session || 'last_name' in session

  // We can determine the count by filtering all company API keys which match the
  // current user identifier.
  const apiKeyCount = useMemo(
    () =>
      apiKeys?.filter(({ created_by }) => created_by === session.identifier)
        .length ?? 0,
    [apiKeys, session.identifier]
  )

  // Update the tab if the URL changes with a match
  const tabValue = useMemo(
    () => TABS.find(({ path }) => path === params['*'])?.value ?? 0,
    [params]
  )

  return (
    <>
      <Typography
        sx={{ textTransform: hasFirstOrLastName ? 'capitalize' : 'none' }}
        variant="h4"
      >
        {getUserDisplayName(session)}
      </Typography>
      <IconCounterGroup
        counts={[
          {
            icon: 'key',
            label: 'API Key',
            count: apiKeyCount
          },
          {
            icon: 'users',
            label: 'Team',
            count: teams.length
          }
        ]}
        css={{ marginBottom: theme.space[3] }}
      />
      <Tabs orientation="horizontal" value={tabValue}>
        <Tab
          component={Link}
          data-test-tab="details"
          label={<>Details</>}
          to="details"
        />
        <Tab
          component={Link}
          data-test-tab="security"
          label={<>Security</>}
          to="security"
        />
        {!isPrivateEdition() && (
          <Tab
            component={Link}
            data-test-tab="integrations"
            label={<>Integrations</>}
            to="integrations"
          />
        )}
        <Tab
          component={Link}
          data-test-tab="apikeys"
          disabled={!areApiKeysEnabled}
          label={<>API Keys</>}
          to="apikeys"
        />
      </Tabs>
      <Outlet />
    </>
  )
}

ProfilePage.displayName = 'ProfilePage'
